import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import StateBadge from "@/components/StateBadge";
import { Search, BookOpen, Sparkles, Compass, Shield, Network as NetIcon, Zap, Radio, Layers, Users } from "lucide-react";

/**
 * /explore — Mirrorloops knowledge / literature system.
 * Client-side search across the writings the company is built on.
 */

const ENTRIES = [
  { id: "physical", icon: Compass, tag: "Mission", title: "Advertising for the Physical World.", body: "Mirrorloops treats the physical environment — screens, walls, kiosks, projections — as a programmable medium. Every place is context. Every moment is programmable. Every screen is an endpoint." },
  { id: "loop", icon: Radio, tag: "Core Concept", title: "The Mirror Loop.", body: "Observe reality → enter reality → receive feedback → learn → change → return. Advertising becomes a feedback system, not a broadcast. This is the literal Mirrorloop." },
  { id: "programmable", icon: Zap, tag: "Product Principle", title: "Advertising as Executable Logic.", body: "Ads are not fixed assets. They are rules: WHEN condition SHOW creative. Weather, time, event, inventory, venue — all become inputs. Delivery is a decision, not a schedule." },
  { id: "moments", icon: Sparkles, tag: "Media Model", title: "Buy Moments — not just Placements.", body: "You aren't buying a screen. You are buying a moment when a place, a person, a mood, and a message align. This is what makes physical media powerful again." },
  { id: "endpoint", icon: NetIcon, tag: "Architecture", title: "Every Screen Is an Endpoint.", body: "Standard handle: mirrorloops://country/city/venue/screen. Every endpoint publishes its capabilities, availability, and rules. This is the beginning of a physical internet for media." },
  { id: "mpmp", icon: Layers, tag: "Labs · Roadmap", title: "The Physical Media Protocol.", body: "MPMP proposes a common address, permission, and verification layer for physical inventory. Addressable · discoverable · machine-readable · permissioned · programmable · measurable · verifiable." },
  { id: "measurement", icon: BookOpen, tag: "Measurement", title: "Observed. Modelled. Inferred.", body: "Never make uncertain measurement look certain. We separate what we observed (proof of play), what we modelled (reach & frequency with confidence), and what we inferred (store visits, lift, sales)." },
  { id: "context", icon: Shield, tag: "Ethics", title: "Context — not Surveillance.", body: "Privacy is architectural, not a footer disclaimer. Contextual signals · aggregated movement · consented first-party data · short retention. No face recognition. No individual tracking. No biometric profiling." },
  { id: "audiences", icon: Users, tag: "Mission", title: "For Artists, Businesses, and People.", body: "Advertising should not require a media plan. Mirrorloops opens the physical media network to musicians, filmmakers, local causes, small brands, and individuals — with the same rules and the same proof as global buyers." },
  { id: "labs-studios", icon: Layers, tag: "Corporate", title: "Labs × Studios.", body: "Labs understands and builds the system. Studios creates what travels through it. OBSERVE → UNDERSTAND → CREATE → DEPLOY → MEASURE → LEARN → OBSERVE. The corporate loop mirrors the media loop." },
  { id: "intelligence", icon: Sparkles, tag: "AI", title: "Mirrorloops Intelligence.", body: "AI that explains why. Every suggestion inside Mirrorloops surfaces its reasoning — accept, modify, or ignore. Never a black box. Never surveillance. Never certainty theatre." },
  { id: "revolution", icon: Compass, tag: "Manifesto", title: "A Revolution in Advertising.", body: "Not a slogan. A slow, thoughtful rewrite of how physical communication works — one node, one endpoint, one honest measurement at a time." },
];

const TAGS = [...new Set(ENTRIES.map((e) => e.tag))];

export default function Explore() {
  const [q, setQ] = useState("");
  const [tag, setTag] = useState(null);
  const results = useMemo(() => {
    const s = q.toLowerCase();
    return ENTRIES.filter(
      (e) =>
        (!tag || e.tag === tag) &&
        (!s ||
          e.title.toLowerCase().includes(s) ||
          e.body.toLowerCase().includes(s) ||
          e.tag.toLowerCase().includes(s))
    );
  }, [q, tag]);

  return (
    <PublicShell>
      <section className="pt-28 md:pt-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-14">
          <div className="flex items-center gap-3">
            <StateBadge state="BETA" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              Explore · the ideas behind Mirrorloops
            </span>
          </div>
          <h1 className="mt-6 font-display font-black tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            The literature.
          </h1>
          <p className="mt-6 max-w-2xl text-white/60 text-lg">
            Every core idea, principle, and vocabulary that Mirrorloops is
            built on — searchable, quotable, and public. Read one entry; the
            rest connect on their own.
          </p>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
          <div className="flex items-center gap-3 border border-white/10 bg-white/[0.02] px-4 h-12">
            <Search size={16} className="text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search for context, proof, loop, programmable…"
              data-testid="explore-search"
              className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30"
            />
            {q && (
              <button onClick={() => setQ("")} className="text-[10px] uppercase tracking-[0.22em] text-white/40 hover:text-white">
                clear
              </button>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-1">
            <TagBtn active={!tag} label="All" onClick={() => setTag(null)} testid="explore-tag-all" />
            {TAGS.map((t) => (
              <TagBtn
                key={t}
                active={tag === t}
                label={t}
                onClick={() => setTag(t)}
                testid={`explore-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              />
            ))}
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10" data-testid="explore-grid">
            {results.length === 0 && (
              <div className="col-span-full p-10 text-center text-white/40 bg-[#0A0908]">
                Nothing matches "{q}" in <span className="text-white">{tag || "any tag"}</span>.
              </div>
            )}
            {results.map((e) => {
              const I = e.icon;
              return (
                <article
                  key={e.id}
                  data-testid={`explore-entry-${e.id}`}
                  className="bg-[#0A0908] p-8 min-h-[220px] hover:bg-white/[0.03] transition"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#E8D6A3] font-mono">
                      {e.tag}
                    </span>
                    <I size={16} className="text-white/40" />
                  </div>
                  <h3 className="font-display font-extrabold text-2xl leading-tight mb-3">
                    {e.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">{e.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 text-center">
          <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl">
            Read once. Cite forever.
          </h2>
          <Link
            to="/register"
            className="mt-8 inline-flex items-center gap-2 px-6 py-4 bg-white text-black font-bold"
          >
            Start with Mirrorloops
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}

function TagBtn({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] uppercase tracking-[0.22em] px-3 py-1.5 border transition ${
        active
          ? "border-[#E8D6A3] text-[#E8D6A3] bg-[#D4B878]/10"
          : "border-white/10 text-white/50 hover:border-white/30"
      }`}
    >
      {label}
    </button>
  );
}
